<template>
  <div>
    <!-- <div class="code-badge">
      PM-SA-SGX-WCNT-260001 DOP: January 2026
    </div> -->

    <div v-if="loading" class="skeleton-container">
      <div class="skeleton-card" v-for="n in 6" :key="n">
        <div class="skeleton-video"></div>
        <div class="skeleton-info">
          <div class="skeleton-likes"></div>
          <div class="skeleton-views">
            <div class="skeleton-text"></div>
            <div class="skeleton-text"></div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="error">{{ error }}</div>

    <div v-else>
      <swiper
        direction="vertical"
        :mousewheel="{ forceToAxis: true, releaseOnEdges: true, sensitivity: 0.5 }"
        :slides-per-view="1"
        :resistance-ratio="0"
        :speed="400"
        :modules="modules"
        class="mySwiper"
        @slideChange="onSlideChange"
      >
        <swiper-slide v-for="(post, index) in posts" :key="post.id">
          <div class="post-card">
            <div class="video-wrapper">
              <video
                :src="`https://be-staging.shing-shorts.com/${post.video_url}`"
                :data-video-id="post.id"
                playsinline
                :muted="isMuted"
                class="post-video"
                @timeupdate="handleTimeUpdate($event, index)"
                @ended="handleVideoEnded"
                @loadedmetadata="handleVideoLoaded($event, index)"
              ></video>
               <div class=" vertical-badge" >
      PM-SA-SGX-WCNT-260001 DOP: January 2026
    </div>

              <div class="custom-progress-bar">
                <div
                  class="progress"
                  :style="{ width: videoProgress[index] + '%' }"
                ></div>
              </div>

              <div
                v-if="promptVisibleIndex === index"
                class="swipe-prompt"
              >
                Swipe Up for more videos
              </div>

              <button class="mute-button" @click="toggleMute">
                <i :class="isMuted ? 'fas fa-volume-mute' : 'fas fa-volume-up'"></i>
              </button>
            </div>

            <div class="marquee-container" :key="marqueeKey[index]">
              <div
                class="marquee-content"
                :style="{ animationDuration: marqueeDuration[index] + 's' }"
              >
                For more information, please refer to the prescribing information or contact GSK:
                P. O Box 58558, Jeddah, 21544, Kingdom of Saudi Arabia. Telephone: +966 12 633 6666 or via gcc.medinfo@gsk.com

                &nbsp;

                To report Adverse Events associated with the use of GSK product(s), please contact us via saudi.safety@gsk.com

                &nbsp;

                To report Product quality related complaint(s) associated with the use of GSK product(s), please contact us via Gulf.KSA-Product-Complaints@gsk.com

                &nbsp;

                Trademarks are owned by or licensed to the GSK group of companies. ©2023 GSK group of companies or its licensor.
                &nbsp;
                PM-SA-SGX-WCNT-260001 DOP: January 2026
                
               
              </div>
            </div>

            <div class="post-info" style="margin-top: 10px">
              <div class="likes-views">
                <button @click="toggleLike(post.id)">
                  <i :class="post.liked ? 'fa-solid fa-heart' : 'fa-regular fa-heart'"></i>
                  {{ post.likes }} Likes
                </button>
                <div class="right-info">
                  <span><i class="fas fa-clock"></i> {{ calculateDaysAgo(post.created_at) }} Days ago</span>
                  <span><i class="fas fa-play"></i> {{ post.views }} Views</span>
                </div>
              </div>
            </div>
          </div>
        </swiper-slide>
      </swiper>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Mousewheel } from 'swiper/modules'
import 'swiper/css'

const modules = [Mousewheel]
const posts = ref([])
const loading = ref(true)
const error = ref(null)
const isMuted = ref(true)
const promptVisibleIndex = ref(null)
const viewedVideos = ref([])
const processingVideos = ref(new Set())
const route = useRoute()
const videoProgress = ref([])
const marqueeDuration = ref([]) 
const marqueeKey = ref([])     

const getLikedVideos = (title) => {
  const key = title ? `likedVideos_${title}` : 'likedVideos_default'
  const data = localStorage.getItem(key)
  return data ? JSON.parse(data) : []
}

const saveLikedVideo = (id, title) => {
  const key = title ? `likedVideos_${title}` : 'likedVideos_default'
  const liked = getLikedVideos(title)
  if (!liked.includes(id)) {
    liked.push(id)
    localStorage.setItem(key, JSON.stringify(liked))
  }
}

const removeLikedVideo = (id, title) => {
  const key = title ? `likedVideos_${title}` : 'likedVideos_default'
  const liked = getLikedVideos(title)
  const updated = liked.filter((x) => x !== id)
  localStorage.setItem(key, JSON.stringify(updated))
}

const toggleLike = async (id) => {
  const post = posts.value.find((p) => p.id === id)
  if (!post) return

  const key = route.params.title

  if (post.liked) {
    post.liked = false
    post.likes--
    removeLikedVideo(id, key)
    try {
      await fetch(`https://be-staging.shing-shorts.com/api/videos/${id}/unlike`)
    } catch {
      post.liked = true
      post.likes++
      saveLikedVideo(id, key)
    }
  } else {
    post.liked = true
    post.likes++
    saveLikedVideo(id, key)
    try {
      await fetch(`https://be-staging.shing-shorts.com/api/videos/${id}/like`)
    } catch {
      post.liked = false
      post.likes--
      removeLikedVideo(id, key)
    }
  }
}

const incrementView = async (id) => {
  const videoIdInt = parseInt(id)
  if (viewedVideos.value.includes(videoIdInt) || processingVideos.value.has(videoIdInt)) return
  processingVideos.value.add(videoIdInt)

  try {
    const res = await fetch(`https://be-staging.shing-shorts.com/api/videos/${id}/view`)
    if (res.ok) {
      const result = await res.json()
      viewedVideos.value.push(videoIdInt)
      const post = posts.value.find((p) => p.id === id)
      if (post) post.views = result.data?.views ?? post.views + 1
    }
  } catch (err) {
    console.error('View error', err)
  } finally {
    processingVideos.value.delete(videoIdInt)
  }
}

const toggleMute = () => {
  isMuted.value = !isMuted.value
  const videos = document.querySelectorAll('video')
  videos.forEach((video) => {
    video.muted = isMuted.value
  })
}

const calculateDaysAgo = (createdAt) => {
  const created = new Date(createdAt)
  const now = new Date()
  return Math.floor((now - created) / (1000 * 3600 * 24))
}

const handleVideoLoaded = (event, index) => {
  const video = event.target
  const duration = video.duration || 10   
  
  let anim

  if (duration >= 30) {
    anim = 35
  } else if (duration >= 15) {
    anim = 25
  } else {
    anim = Math.max(duration * 2.5, 20) 
  }

  marqueeDuration.value[index] = anim
}


// عند تغيير السلايد
const onSlideChange = (swiper) => {
  const currentIndex = swiper.activeIndex
  const slides = document.querySelectorAll('.swiper-slide')
  const currentSlide = slides[currentIndex]
  const currentVideo = currentSlide?.querySelector('video')

  if (currentVideo) {
    slides.forEach((slide) => {
      const video = slide.querySelector('video')
      if (video && video !== currentVideo) {
        video.pause()
      }
    })

    currentVideo.muted = isMuted.value
    currentVideo.currentTime = 0
    currentVideo.play().catch(() => {})

    const post = posts.value[currentIndex]
    if (post) incrementView(post.id)

    marqueeKey.value[currentIndex] = (marqueeKey.value[currentIndex] || 0) + 1
  }
}


const handleVideoEnded = () => {
  const swiperEl = document.querySelector('.mySwiper')?.swiper
  if (swiperEl) {
    swiperEl.slideNext()
  }
}


const handleTimeUpdate = (event, index) => {
  const video = event.target
  const currentTime = video.currentTime
  const duration = video.duration

  if (duration > 0) {
    videoProgress.value[index] = (currentTime / duration) * 100
  }

  if (duration - currentTime <= 3) {
    promptVisibleIndex.value = index
  } else if (promptVisibleIndex.value === index) {
    promptVisibleIndex.value = null
  }
}

const fetchData = async () => {
  try {
    const res = await fetch('https://be-staging.shing-shorts.com/api/videos')
    const data = await res.json()
    const likedIds = getLikedVideos(route.params.title)
    const categoryOrder = Object.keys(data)
    const categoryMap = {}

    for (const cat of categoryOrder) {
      categoryMap[cat] = data[cat].map((v) => ({
        ...v,
        liked: likedIds.includes(v.id),
        _category: cat,
      }))
    }

    const result = []
    let anyLeft = true

    while (anyLeft) {
      anyLeft = false
      for (const cat of categoryOrder) {
        const videos = categoryMap[cat]
        if (videos.length > 0) {
          const randomIndex = Math.floor(Math.random() * videos.length)
          const [video] = videos.splice(randomIndex, 1)
          if (video) {
            result.push(video)
            anyLeft = true
          }
        }
      }
    }

    posts.value = result

    videoProgress.value = new Array(result.length).fill(0)
    marqueeDuration.value = new Array(result.length).fill(30)
    marqueeKey.value = new Array(result.length).fill(0)
  } catch (err) {
    error.value = 'Failed to load videos.'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  document.title = 'Shingrix'
})

onMounted(() => {
  fetchData().then(() => {
    const firstSlide = document.querySelector('.swiper-slide-active')
    const firstVideo = firstSlide?.querySelector('video')
    if (firstVideo) {
      firstVideo.muted = isMuted.value
      firstVideo.play().catch(() => {})
    }
  })
})
</script>

<style scoped>
.logo-container {
  position: fixed;
  top: 10px;
  left: 40px;
  z-index: 1000;
}

/* ماركيه جديدة */
.marquee-container {
  width: 100%;
  overflow: hidden;
  position: relative;
  background-color: rgba(106, 107, 110, 0.7);
  padding: 5px 0;
  margin-top: 5px;
  /* border-radius: 4px; */
  /* height: 40px; */
  display: flex;
  align-items: center;
}

.marquee-content {
  display: inline-block;
  white-space: nowrap;
  animation-name: marquee;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  /* padding-left: 100%; */
  font-size: 10px;
  font-weight: bold;
  color: white;
  line-height: 1.4;
  text-shadow: none;
}

@keyframes marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
}

.marquee-container:hover .marquee-content {
  animation-play-state: paused;
}

.logo {
  height: 60px;
  width: auto;
  cursor: pointer;
}

.mySwiper {
  width: 100%;
  height: 100vh;
}

.swiper-slide {
  display: flex;
  justify-content: center;
  align-items: center;
}

.post-card {
  background: white;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  max-width: 350px;
  margin: 20px auto;
}

.video-wrapper {
  position: relative;
}

.post-video {
  width: 100%;
  border-radius: 10px;
  pointer-events: auto;
  object-fit: contain;
}

.mute-button {
  position: absolute;
  bottom: 17px;
  right: 10px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  padding: 6px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
}

.likes-views {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.likes-views button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 5px;
}

.right-info {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
  color: #9e9e9e;
}

.right-info span {
  display: flex;
  align-items: center;
  gap: 5px;
}

.error {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
}

.swipe-prompt {
  position: absolute;
  top: 30%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  z-index: 10;
  white-space: nowrap;
  font-size: 18px;
  font-weight: bold;
  animation: slideDownUp 3s ease-in-out forwards;
  text-shadow: 0 0 4px black, 0 0 6px black;
}

@keyframes slideDownUp {
  0% {
    opacity: 0;
    transform: translate(-50%, -80%);
  }
  15% {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
  85% {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -70%);
  }
}

/*.code-badge {
  position: fixed;
  bottom: 10px;
  left: 30px;
  z-index: 1100;
  font-size: 12px;
  font-weight: bold;
  color: black;
  padding: 4px 8px;
  border-radius: 4px;
  text-align: right;
  line-height: 1.4;
}*/
.vertical-badge {
  position: absolute;
  left: 10px;              
  bottom: 6px;            
  transform: translateY(-50%) rotate(-90deg);
  transform-origin: left top;
  padding: 2px ;
  font-size: 8px;
  font-weight: bold;
  /* background: rgba(255, 255, 255, 0.9); */
  color: black;
  white-space: nowrap;
}

/* mobile */
@media (max-width: 768px) {
  .swiper-slide {
    align-items: flex-start !important;
    padding-top: 0 !important;
  }

  .post-card {
    max-width: 85%;
    margin: 60px auto 0;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 10px;
    border-radius: 8px;
    max-height: none;
    overflow: visible;
  }

  .post-video {
    width: 100%;
    max-height: 60vh;
    object-fit: contain;
  }

  .marquee-container {
    height: 35px;
    padding: 3px 0;
  }

  .marquee-content {
    font-size: 8px;
    line-height: 1.2;
  }

  .likes-views {
    font-size: 12px;
  }

  .right-info {
    gap: 6px;
  }

  /*.code-badge {
    font-size: 12px;
    bottom: 8px;
    left: 8px;
  }*/
    .vertical-badge {
    font-size: 8px;                 
    left: 30px;                     
    bottom: 0px;   
                     
    transform: translateY(-50%) rotate(-90deg);
    /* padding: 2px 4px;               */
  }
}

.custom-progress-bar {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 5px;
  position: relative;
}

.custom-progress-bar .progress {
  height: 100%;
  background-color: black;
  width: 0%;
  transition: width 0.2s linear;
}
</style>

<style>
body {
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  background-color: #fafafa;
  font-family: 'GSK Precision', sans-serif;
}

.likes-views button {
  font-family: 'GSK Precision', sans-serif;
}
</style>
